/**
 * @fileoverview Authentication Context for managing user authentication, authorization, and API requests.
 *
 * This context provides comprehensive authentication functionality including:
 * - User login/logout with 2FA support
 * - Permission-based access control
 * - Token management and refresh
 * - API request wrapper with authentication
 * - Password reset functionality
 * - User session management
 *
 * Used throughout the application for:
 * - Protected routes and components
 * - Permission-based UI rendering
 * - Authenticated API calls
 * - User profile management
 * - Authentication guards
 */

import { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useNotify } from './NotificationContext';
import { getGlobalTracker } from '../utils/ActivityTracker';
import { debugLog, debugWarn, debugError } from '../utils/debug';

/**
 * React Context for authentication state and methods
 * @type {React.Context<AuthContextValue>}
 */
const AuthContext = createContext();

/**
 * Custom hook to access the authentication context.
 * Must be used within an AuthProvider component.
 *
 * @function useAuth
 * @returns {AuthContextValue} The authentication context value containing user state and methods
 * @throws {Error} When used outside of AuthProvider
 *
 * @example
 * ```jsx
 * function MyComponent() {
 *   const { user, login, hasPermission } = useAuth();
 *
 *   if (!user) {
 *     return <LoginForm onLogin={login} />;
 *   }
 *
 *   return <Dashboard />;
 * }
 * ```
 */
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

/**
 * Helper function to check if an error is an AppPausedError.
 * Use this in catch blocks to silently ignore errors caused by app inactivity.
 *
 * @param {Error} error - The error to check
 * @returns {boolean} True if the error is an AppPausedError
 *
 * @example
 * ```jsx
 * try {
 *   await apiRequest('/some-endpoint');
 * } catch (err) {
 *   if (isAppPausedError(err)) return;
 *   error('Network error');
 * }
 * ```
 */
export const isAppPausedError = (error) => {
    return error?.name === 'AppPausedError';
};

/**
 * Authentication Provider component that manages user authentication state and provides
 * authentication methods to child components.
 *
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components that will have access to auth context
 * @returns {JSX.Element} Provider component wrapping children with authentication context
 *
 * @example
 * ```jsx
 * function App() {
 *   return (
 *     <AuthProvider>
 *       <Router>
 *         <Routes>
 *           <Route path="/dashboard" element={<Dashboard />} />
 *         </Routes>
 *       </Router>
 *     </AuthProvider>
 *   );
 * }
 * ```
 */
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [userLoading, setUserLoading] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false); // eslint-disable-line no-unused-vars
    const [isAppPaused, setIsAppPaused] = useState(false); // Track if app requests are paused due to inactivity

    const { error } = useNotify(); // eslint-disable-line no-unused-vars

    // Ref to track the pause timeout
    const pauseTimeoutRef = useRef(null);

    /*
     * Mirror of isAppPaused that updates synchronously.
     *
     * setIsAppPaused(false) is an async React state update, but apiRequest is a
     * useCallback closing over isAppPaused. On resume, mount/effect refetches
     * run in the same tick as the visibility event, still holding the closure
     * where isAppPaused === true — so they threw AppPausedError and call sites
     * surfaced it as "Network error while loading…". The ref is read instead of
     * the state value so resume is visible to in-flight callers immediately.
     */
    const isAppPausedRef = useRef(false);
    const setAppPaused = useCallback((paused) => {
        isAppPausedRef.current = paused;
        setIsAppPaused(paused);
    }, []);

    // Track tab visibility and pause requests after 5 minutes of being hidden
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                // Tab is hidden - start 5 minute timer to pause requests
                pauseTimeoutRef.current = setTimeout(() => {
                    debugLog('🔒 App paused - tab hidden for 5 minutes');
                    setAppPaused(true);
                }, 300000); // 5 minutes
            } else {
                // Tab is visible - clear timer and resume requests
                if (pauseTimeoutRef.current) {
                    clearTimeout(pauseTimeoutRef.current);
                    pauseTimeoutRef.current = null;
                }
                if (isAppPausedRef.current) {
                    debugLog('🔓 App resumed - tab visible again');
                    setAppPaused(false);
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (pauseTimeoutRef.current) {
                clearTimeout(pauseTimeoutRef.current);
            }
        };
        // Reads pause state via isAppPausedRef, so the listener does not need
        // to be torn down and re-registered on every pause/resume toggle.
    }, [setAppPaused]);

    // Environment configuration
    const DEV = process.env.REACT_APP_DEV === 'true' || process.env.NODE_ENV === 'development';

    // In dev: Use same subdomain as frontend for API calls (for cookie same-site)
    // e.g., demo.localhost:3000 -> demo.localhost:5050
    const getDevApiUrl = () => {
        if (typeof window !== 'undefined') {
            const hostname = window.location.hostname;
            // Use the same hostname but different port for API
            return `http://${hostname}:5050`;
        }
        return 'http://localhost:5050';
    };

    const APP_URL = DEV ? getDevApiUrl() : 'https://api.clockwisecpa.app';
    const FILE_APP_URL = DEV ? 'http://localhost:5151' : 'https://fileapi.clockwisecpa.app';

    // Store pending requests during token refresh
    const refreshPromiseRef = useRef(null);

    // Listen for test session expired event (dev mode only)
    useEffect(() => {
        if (DEV) {
            const handleTestSessionExpired = () => {
                debugLog('🧪 Test: Triggering session expired redirect');
                window.location.href = '/login?expired=true';
            };

            window.addEventListener('test-session-expired', handleTestSessionExpired);

            return () => {
                window.removeEventListener('test-session-expired', handleTestSessionExpired);
            };
        }
    }, [DEV]);

    /**
     * Utility function to get cookie values by name
     * @param {string} name - The name of the cookie to retrieve
     * @returns {string} The cookie value or empty string if not found
     */
    const getCookie = useCallback((name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return '';
    }, []);

    /**
     * Attempt to refresh the access token using the refresh token
     * Uses a singleton pattern to prevent multiple simultaneous refresh requests
     * @returns {Promise<boolean>} True if refresh was successful, false otherwise
     */
    const attemptTokenRefresh = useCallback(async () => {
        // If we're already refreshing, return the existing promise
        if (refreshPromiseRef.current) {
            return refreshPromiseRef.current;
        }

        // If we're logging out, don't attempt refresh
        if (isLoggingOut) {
            return false;
        }

        // Check if we have a refresh token
        const refreshToken = getCookie('csrf_refresh_token');
        if (!refreshToken) {
            setUser(null);
            return false;
        }

        // Create the refresh promise
        const refreshPromise = (async () => {
            setIsRefreshing(true);

            try {
                // Make direct fetch call to avoid recursion with apiRequest
                const url = `${APP_URL}/refresh`;
                debugLog('🔄 Attempting token refresh...');
                const response = await fetch(url, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': refreshToken
                    }
                });

                debugLog('🔄 Refresh response status:', response.status);

                if (response.ok) {
                    const userData = await response.json();
                    debugLog('✅ Token refresh succeeded');
                    setUser(userData);
                    return true;
                }

                // Refresh failed - check if it's because refresh token expired
                if (response.status === 401) {
                    // Refresh token has expired - redirect to login with expired flag
                    debugLog('🔐 Refresh token expired (401) - redirecting to login');
                    setUser(null);
                    clearAuthCookies();
                    window.location.href = '/login?expired=true';
                    return false;
                }

                // Clear user state for other failures
                debugLog('❌ Token refresh failed with status:', response.status);
                setUser(null);
                return false;

            } catch (err) {
                debugError('❌ Token refresh failed with error:', err);
                setUser(null);
                return false;
            } finally {
                setIsRefreshing(false);
                // Clear the refresh promise reference
                refreshPromiseRef.current = null;
            }
        })();

        // Store the promise so other requests can wait for it
        refreshPromiseRef.current = refreshPromise;

        return refreshPromise;
    }, [isLoggingOut, getCookie, APP_URL]);

    /**
     * Enhanced API request helper with authentication, CSRF protection, and automatic token refresh
     * @param {string} endpoint - The API endpoint path
     * @param {Object} [options={}] - Fetch options
     * @param {string} [options.method] - HTTP method
     * @param {Object} [options.headers] - Additional headers
     * @param {string} [options.site] - Site identifier ('timefiles' for file API)
     * @param {string|FormData} [options.body] - Request body
     * @param {boolean} [options._isRetry] - Internal flag to prevent infinite retry loops
     * @param {boolean} [options.bypassPause] - If true, request will be made even when app is paused
     * @returns {Promise<Response>} The fetch response
     * @throws {Error} When the request fails or app is paused
     */
    const apiRequest = useCallback(async (endpoint, options = {}) => {
        // If app is paused and this isn't a bypass request, reject the request
        // Critical endpoints like login/logout/refresh should use bypassPause: true
        const criticalEndpoints = ['/login', '/logout', '/refresh', '/user'];
        const isCritical = criticalEndpoints.some(e => endpoint.startsWith(e)) || options.bypassPause;

        /*
         * A visible tab is never paused. The resume path sets isAppPausedRef
         * synchronously, but a request can still be issued from a closure or an
         * effect that queued before that ran; checking visibility here means a
         * user who is looking at the app never gets their refetch rejected.
         * Also self-heal the flag so later requests take the fast path.
         */
        if (isAppPausedRef.current && typeof document !== 'undefined'
            && document.visibilityState === 'visible') {
            isAppPausedRef.current = false;
            setIsAppPaused(false);
        }

        if (isAppPausedRef.current && !isCritical) {
            const error = new Error('App is paused due to inactivity');
            error.name = 'AppPausedError';
            throw error;
        }

        let url = `${APP_URL}${endpoint}`;

        // Don't set Content-Type for FormData (browser sets it automatically with boundary)
        const isFormData = options.body instanceof FormData;

        const defaultOptions = {
            credentials: 'include',
            headers: {
                ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
                ...options.headers,
            },
        };

        if(options.site === 'timefiles'){
            url = `${FILE_APP_URL}${endpoint}`;
        }

        // Add CSRF token for non-GET requests
        if (options.method && options.method !== 'GET') {
            defaultOptions.headers['X-CSRF-TOKEN'] = getCookie('csrf_access_token');
        }

        // Add dev organization header if in development mode
        if (DEV) {
            // First check localStorage for dev org switcher
            let orgId = localStorage.getItem('devOrgSubdomain');

            // If not in localStorage, detect from hostname subdomain (e.g., demo.localhost)
            if (!orgId) {
                const hostname = window.location.hostname;
                if (hostname.endsWith('.localhost')) {
                    const subdomain = hostname.split('.')[0];
                    const reserved = ['www', 'api', 'admin', 'fileapi', 'timefiles'];
                    if (!reserved.includes(subdomain)) {
                        orgId = subdomain;
                    }
                }
            }

            if (orgId) {
                defaultOptions.headers['X-Organization-ID'] = orgId;
            }
        }

        // Properly merge options with defaultOptions, ensuring headers are merged not replaced
        const config = {
            ...defaultOptions,
            ...options,
            headers: {
                ...defaultOptions.headers,
                ...(options.headers || {})
            }
        };

        try {
            const response = await fetch(url, config);

            // If we get a 401 and this isn't already a retry, attempt token refresh
            if (response.status === 401 && !options._isRetry && endpoint !== '/refresh' && endpoint !== '/login') {
                debugLog('🚨 Got 401 for endpoint:', endpoint, '- attempting token refresh');

                // Try to refresh the token
                const refreshed = await attemptTokenRefresh();

                debugLog('🔄 Token refresh result:', refreshed ? 'Success' : 'Failed');

                if (refreshed) {
                    // Update the CSRF token with the new one
                    if (options.method && options.method !== 'GET') {
                        config.headers['X-CSRF-TOKEN'] = getCookie('csrf_access_token');
                    }

                    debugLog('🔁 Retrying request to', endpoint, 'with new token');
                    // Retry the request with the new token
                    return apiRequest(endpoint, { ...options, _isRetry: true });
                } else {
                    debugLog('⚠️ Token refresh failed - returning 401 response');
                }
            }

            return response;
        } catch (err) {
            // Don't log AbortError as it's expected when requests are cancelled
            if (err.name === 'AbortError') {
                // Silently throw AbortError without logging
                throw err;
            }
            debugError('API Request Error:', err);
            throw err;
        }
        // isAppPaused intentionally omitted: pause state is read from
        // isAppPausedRef so apiRequest keeps a stable identity and callers
        // never hold a closure with a stale paused value.
    }, [APP_URL, FILE_APP_URL, DEV, getCookie, attemptTokenRefresh]);

    /**
     * Retrieves the current user data with caching and error handling options
     * @param {Object} [options={}] - Configuration options
     * @param {boolean} [options.cache=true] - Use cached user if available
     * @param {boolean} [options.forceRefresh=false] - Force a fresh API call
     * @param {boolean} [options.throwOnError=false] - Throw errors instead of returning null
     * @returns {Promise<Object|null>} The user object or null if not authenticated
     * @throws {Error} When throwOnError is true and an error occurs
     */
    const getUser = useCallback(async (options = {}) => {
        const {
            cache = true,           // Use cached user if available
            forceRefresh = false,   // Force a fresh API call
            throwOnError = false    // Throw errors instead of returning null
        } = options;

        // Don't fetch user if we're in the middle of logging out
        if (isLoggingOut) {
            if (throwOnError) {
                throw new Error('Currently logging out');
            }
            return null;
        }

        // Return cached user if available and not forcing refresh
        if (cache && user && !forceRefresh) {
            return user;
        }

        // Check if we have a token
        if (!getCookie('csrf_access_token')) {
            if (throwOnError) {
                throw new Error('No authentication token found');
            }
            setUser(null);
            return null;
        }

        setUserLoading(true);

        try {
            const response = await apiRequest('/user');

            if (response.ok) {
                const userData = await response.json();
                setUser(userData);
                return userData;
            }

            // Check for signature verification failure (JWT secret changed)
            if (response.status === 401 || response.status === 422) {
                try {
                    const errorData = await response.clone().json();
                    if (errorData.msg === 'Signature verification failed') {
                        debugLog('JWT signature verification failed - clearing cookies and redirecting to login');
                        setUser(null);
                        clearAuthCookies();
                        window.location.href = '/login?expired=true';
                        return null;
                    }
                } catch (parseErr) {
                    // Ignore JSON parse errors
                }
            }

            // apiRequest handles token refresh automatically for 401s
            // If we still get a non-ok response here, authentication has failed
            setUser(null);
            if (throwOnError) {
                throw new Error(`Failed to get user: ${response.status}`);
            }
            return null;

        } catch (err) {
            debugError('Error getting user:', err);
            setUser(null);
            if (throwOnError) {
                throw err;
            }
            return null;
        } finally {
            setUserLoading(false);
        }
    }, [user, isLoggingOut]); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * Checks if the current user has a specific permission
     * @param {string} permission - The permission to check
     * @param {Object} [options={}] - Options to pass to getUser
     * @returns {Promise<boolean>} True if user has permission, false otherwise
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const hasPermission = async (permission, options = {}) => {
        if (!permission) {
            debugWarn('hasPermission called without permission parameter');
            return false;
        }

        try {
            // Use centralized getEffectiveRole method
            const roleToCheck = await getEffectiveRole();

            if (!roleToCheck) {
                return false;
            }

            // Check for admin access (all permissions)
            if (roleToCheck['all']) {
                return true;
            }

            // Check specific permission
            return !!roleToCheck[permission];

        } catch (err) {
            debugError('Permission check failed:', err);
            return false;
        }
    };

    /**
     * Requires a specific permission and throws an error if not found
     * @param {string} permission - The permission to require
     * @param {Object} [options={}] - Options to pass to hasPermission
     * @returns {Promise<boolean>} Always returns true if permission exists
     * @throws {Error} When user doesn't have the required permission
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const requirePermission = async (permission, options = {}) => {
        const hasAccess = await hasPermission(permission, options);

        if (!hasAccess) {
            throw new Error(`Permission required: ${permission}`);
        }

        return true;
    };

    /**
     * Refreshes the current user data by forcing a new API call
     * @returns {Promise<boolean>} True if refresh was successful, false otherwise
     */
    const refreshUser = useCallback(async () => {
        try {
            const userData = await getUser({ forceRefresh: true });
            return !!userData;
        } catch (err) {
            return false;
        }
    }, [getUser]);

    /**
     * Authenticates a user with username and password
     * @param {string} username - User's username
     * @param {string} password - User's password
     * @returns {Promise<Object>} Login result object
     * @returns {boolean} returns.success - Whether login was successful
     * @returns {boolean} [returns.requires2FA] - Whether 2FA is required
     * @returns {Object} [returns.user] - User object if login successful
     * @returns {string} [returns.error] - Error message if login failed
     * @returns {string} [returns.message] - Additional message from server
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const login = async (username, password) => {
        if (!username || !password) {
            return { error: 'Username and password are required' };
        }

        debugLog('🔐 Attempting login for user:', username);

        setIsLoading(true);

        try {
            // Check if 2FA bypass is enabled in dev mode
            const bypass2FA = localStorage.getItem('dev2FABypass') === 'true';
            const headers = {
                'Content-Type': 'application/json'
            };

            // Add bypass header if enabled (dev only)
            if (bypass2FA) {
                headers['X-Bypass-2FA'] = 'true';
            }

            const response = await apiRequest('/login', {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({ username, password })
            });

            const data = await response.json();

            if (response.ok) {
                debugLog('✅ Login successful');

                // Check if passkey setup is required (first-time user with no passkeys)
                if (data.requires_passkey_setup) {
                    return {
                        success: true,
                        requiresPasskeySetup: true,
                        tempToken: data.temp_token,
                        hasEmail: data.has_email,
                        passkeyPromptDismissed: data.passkey_prompt_dismissed,
                        user: data.user,
                        message: data.message
                    };
                }

                // Check if 2FA is required (user has passkeys or prefers email)
                if (data.requires_2fa || data.success === '2FA') {
                    return {
                        success: true,
                        requires2FA: true,
                        hasBiometric: data.has_biometric,
                        hasEmail: data.has_email,
                        tempToken: data.temp_token,
                        preferEmail: data.prefer_email,
                        passkeyPromptDismissed: data.passkey_prompt_dismissed,
                        user: data.user,
                        message: data.message
                    };
                }

                // Direct login success (no 2FA)
                setUser(data);
                // Clear any remaining logout state
                setIsLoggingOut(false);
                debugLog('🎉 User logged in successfully');

                // Set user in activity tracker
                const tracker = getGlobalTracker();
                if (tracker && data.id) {
                    tracker.setUser(data.id, data.organization_id);
                    debugLog('✅ Activity tracker user context set');
                }

                return { success: true, user: data };
            }

            debugLog('❌ Login failed:', data.error);
            return { error: data.error || 'Login failed' };

        } catch (err) {
            debugError('Login error:', err);
            return { error: `Network error during login ${err}` };
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Submits a 2FA verification code to complete login
     * @param {string} code - The 8-digit 2FA verification code
     * @returns {Promise<Object>} 2FA submission result
     * @returns {boolean} returns.success - Whether 2FA verification was successful
     * @returns {Object} [returns.user] - User object if verification successful
     * @returns {string} [returns.error] - Error message if verification failed
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const submit2FA = async (code) => {
        if (!code || code.length !== 8) {
            return { error: 'Please enter a valid 8-digit code' };
        }

        setIsLoading(true);

        try {
            const response = await apiRequest('/login', {
                method: 'POST',
                body: JSON.stringify({ '2fa_code': code })
            });

            const data = await response.json();

            if (response.ok) {
                setUser(data);

                // Set user in activity tracker after 2FA success
                const tracker = getGlobalTracker();
                if (tracker && data.id) {
                    tracker.setUser(data.id, data.organization_id);
                    debugLog('✅ Activity tracker user context set (after 2FA)');
                }

                return { success: true, user: data };
            }

            return { error: data.error || 'Invalid verification code' };

        } catch (err) {
            debugError('2FA error:', err);
            return { error: 'Network error during verification' };
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Helper function to clear authentication cookies properly across all domains and paths
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const clearAuthCookies = () => {
        debugLog('🧹 Clearing all authentication cookies...');

        // Clear cookies with proper expiration for different possible paths and domains
        const cookiesToClear = ['csrf_access_token', 'csrf_refresh_token'];
        const domains = [
            window.location.hostname,
            `.${window.location.hostname}`,
            'localhost',
            '.localhost',
            'clockwisecpa.app',
            '.clockwisecpa.app'
        ];
        const paths = ['/', '/login', '/dashboard', '/api'];

        cookiesToClear.forEach(cookieName => {
            // Clear with different path and domain combinations
            paths.forEach(path => {
                domains.forEach(domain => {
                    // Try with domain
                    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain}; SameSite=Lax;`;
                    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain}; SameSite=Strict;`;
                    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain}; SameSite=None; Secure;`;
                    // Try without domain
                    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path};`;
                    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; SameSite=Lax;`;
                    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; SameSite=Strict;`;
                });
            });
            // Also clear without specifying domain/path
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
            document.cookie = `${cookieName}=; Max-Age=0;`;
        });

        debugLog('✅ Cookies cleared');
    };

    // Simplified logout function
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const logout = async () => {
        // Set logout state FIRST before clearing user to prevent redirect race condition
        setIsLoggingOut(true);
        setIsLoading(true);

        // Use setTimeout to ensure isLoggingOut renders before clearing user
        // This prevents the flash of login screen during logout transition
        await new Promise(resolve => setTimeout(resolve, 0));

        try {
            setUser(null);

            // Clear user from activity tracker
            const tracker = getGlobalTracker();
            if (tracker) {
                tracker.setUser(null, null);
                debugLog('✅ Activity tracker user context cleared');
            }

            const response = await apiRequest('/logout', {
                method: 'DELETE'
            });

            clearAuthCookies();

            if (response.status === 204 || response.ok) {
                return { success: true };
            }

            const data = await response.json();
            return { error: data.error || 'Logout failed' };

        } catch (err) {
            debugError('Logout error:', err);
            return { error: 'Network error during logout' };
        } finally {
            setIsLoading(false);
            // Clear logout state after a brief delay to prevent race conditions
            setTimeout(() => setIsLoggingOut(false), 500);
        }
    };

    // Password reset function
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const requestPasswordReset = async (email) => {
        if (!email) {
            return { error: 'Email is required' };
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            return { error: 'Invalid email address' };
        }

        setIsLoading(true);

        try {
            const response = await apiRequest('/reset_password/send', {
                method: 'POST',
                body: JSON.stringify({ email })
            });

            if (response.ok) {
                return { success: true, message: 'Password reset email sent' };
            }

            const data = await response.json();
            return { error: data.error || 'Failed to send reset email' };

        } catch (err) {
            debugError('Password reset error:', err);
            return { error: 'Network error during password reset' };
        } finally {
            setIsLoading(false);
        }
    };

    // WebAuthn / Biometric Authentication Functions
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const authenticateWithBiometric = async (username) => {
        if (!username) {
            return { error: 'Username is required' };
        }

        setIsLoading(true);

        try {
            // Get authentication options from server
            const optionsResponse = await apiRequest('/api/webauthn/authenticate/options', {
                method: 'POST',
                body: JSON.stringify({ username })
            });

            if (!optionsResponse.ok) {
                const data = await optionsResponse.json();
                return { error: data.error || 'Failed to get authentication options' };
            }

            const options = await optionsResponse.json();

            // Import WebAuthn function
            const { authenticateBiometric } = await import('../utils/webauthn');

            // Prompt for biometric authentication
            const credential = await authenticateBiometric(options);

            // Verify authentication with server
            const verifyResponse = await apiRequest('/api/webauthn/authenticate/verify', {
                method: 'POST',
                body: JSON.stringify({
                    username,
                    credential
                })
            });

            if (!verifyResponse.ok) {
                const data = await verifyResponse.json();
                return { error: data.error || 'Authentication failed' };
            }

            const userData = await verifyResponse.json();
            setUser(userData.user || userData);

            // Set user in activity tracker after biometric auth success
            const tracker = getGlobalTracker();
            const userObj = userData.user || userData;
            if (tracker && userObj.id) {
                tracker.setUser(userObj.id, userObj.organization_id);
                debugLog('✅ Activity tracker user context set (after biometric)');
            }

            return { success: true, user: userData.user || userData };

        } catch (err) {
            debugError('Biometric authentication error:', err);
            return { error: err.message || 'Biometric authentication failed' };
        } finally {
            setIsLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const registerBiometric = async (deviceName) => {
        if (!deviceName || !deviceName.trim()) {
            return { error: 'Device name is required' };
        }

        setIsLoading(true);

        try {
            // Get registration options from server
            const optionsResponse = await apiRequest('/api/webauthn/register/options', {
                method: 'POST'
            });

            if (!optionsResponse.ok) {
                const data = await optionsResponse.json();
                return { error: data.error || 'Failed to get registration options' };
            }

            const options = await optionsResponse.json();

            // Import WebAuthn function
            const { registerBiometric: registerBiometricUtil } = await import('../utils/webauthn');

            // Prompt for biometric registration
            const credential = await registerBiometricUtil(options);

            // Verify registration with server
            const verifyResponse = await apiRequest('/api/webauthn/register/verify', {
                method: 'POST',
                body: JSON.stringify({
                    credential,
                    device_name: deviceName.trim()
                })
            });

            if (!verifyResponse.ok) {
                const data = await verifyResponse.json();
                return { error: data.error || 'Registration failed' };
            }

            const result = await verifyResponse.json();

            return { success: true, credential: result.credential };

        } catch (err) {
            debugError('Biometric registration error:', err);
            return { error: err.message || 'Biometric registration failed' };
        } finally {
            setIsLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const getBiometricCredentials = async () => {
        try {
            const response = await apiRequest('/api/webauthn/credentials', {
                method: 'GET'
            });

            if (!response.ok) {
                const data = await response.json();
                return { error: data.error || 'Failed to get credentials' };
            }

            const data = await response.json();
            return { success: true, credentials: data.credentials };

        } catch (err) {
            debugError('Error fetching biometric credentials:', err);
            return { error: 'Failed to fetch credentials' };
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const revokeBiometricCredential = async (credentialId) => {
        if (!credentialId) {
            return { error: 'Credential ID is required' };
        }

        try {
            const response = await apiRequest(`/api/webauthn/credentials/${credentialId}`, {
                method: 'DELETE'
            });

            if (!response.ok) {
                const data = await response.json();
                return { error: data.error || 'Failed to revoke credential' };
            }

            return { success: true };

        } catch (err) {
            debugError('Error revoking biometric credential:', err);
            return { error: 'Failed to revoke credential' };
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const updateBiometricCredential = async (credentialId, newName) => {
        if (!credentialId) {
            return { error: 'Credential ID is required' };
        }

        if (!newName || !newName.trim()) {
            return { error: 'New device name is required' };
        }

        try {
            const response = await apiRequest(`/api/webauthn/credentials/${credentialId}`, {
                method: 'PATCH',
                body: JSON.stringify({
                    device_name: newName.trim()
                })
            });

            if (!response.ok) {
                const data = await response.json();
                return { error: data.error || 'Failed to update credential' };
            }

            const data = await response.json();
            return { success: true, credential: data.credential };

        } catch (err) {
            debugError('Error updating biometric credential:', err);
            return { error: 'Failed to update credential' };
        }
    };

    // ============================================================================
    // Passkey Setup During Login (uses temp_token instead of JWT)
    // These functions are for first-time passkey setup when user has no passkeys
    // ============================================================================

    /**
     * Setup a passkey during login flow (before JWT is issued)
     * Uses temp_token for authentication instead of JWT
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const setupPasskeyDuringLogin = async (tempToken, deviceName) => {
        if (!tempToken) {
            return { error: 'Session expired, please login again' };
        }

        if (!deviceName || !deviceName.trim()) {
            return { error: 'Device name is required' };
        }

        setIsLoading(true);

        try {
            // Get registration options from server (using temp_token)
            const optionsResponse = await fetch(`${APP_URL}/api/webauthn/setup/options`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ temp_token: tempToken })
            });

            if (!optionsResponse.ok) {
                const data = await optionsResponse.json();
                return { error: data.error || 'Failed to get registration options' };
            }

            const options = await optionsResponse.json();

            // Import WebAuthn function
            const { registerBiometric: registerBiometricUtil } = await import('../utils/webauthn');

            // Prompt for biometric registration
            const credential = await registerBiometricUtil(options);

            // Verify registration with server and complete login
            const verifyResponse = await fetch(`${APP_URL}/api/webauthn/setup/verify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    temp_token: tempToken,
                    credential,
                    device_name: deviceName.trim()
                })
            });

            if (!verifyResponse.ok) {
                const data = await verifyResponse.json();
                return { error: data.error || 'Passkey registration failed' };
            }

            const userData = await verifyResponse.json();
            setUser(userData.user || userData);

            // Set user in activity tracker
            const tracker = getGlobalTracker();
            const userObj = userData.user || userData;
            if (tracker && userObj.id) {
                tracker.setUser(userObj.id, userObj.organization_id);
                debugLog('✅ Activity tracker user context set (after passkey setup)');
            }

            return { success: true, user: userData.user || userData };

        } catch (err) {
            debugError('Passkey setup error:', err);
            return { error: err.message || 'Passkey setup failed' };
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Skip passkey setup and use email 2FA instead
     * Marks user preference and sends email code
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const skipPasskeySetup = async (tempToken) => {
        if (!tempToken) {
            return { error: 'Session expired, please login again' };
        }

        setIsLoading(true);

        try {
            const response = await fetch(`${APP_URL}/api/webauthn/setup/skip`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ temp_token: tempToken })
            });

            if (!response.ok) {
                const data = await response.json();
                return { error: data.error || 'Failed to skip passkey setup' };
            }

            const data = await response.json();
            return { success: true, message: data.message || 'Email code sent' };

        } catch (err) {
            debugError('Skip passkey setup error:', err);
            return { error: 'Failed to skip passkey setup' };
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Authenticate with passkey during 2FA flow (using temp_token)
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const authenticatePasskeyWithToken = async (tempToken) => {
        if (!tempToken) {
            return { error: 'Session expired, please login again' };
        }

        setIsLoading(true);

        try {
            // Get authentication options from server
            const optionsResponse = await fetch(`${APP_URL}/api/webauthn/auth/options`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ temp_token: tempToken })
            });

            if (!optionsResponse.ok) {
                const data = await optionsResponse.json();
                return { error: data.error || 'Failed to get authentication options' };
            }

            const options = await optionsResponse.json();

            // Import WebAuthn function
            const { authenticateBiometric } = await import('../utils/webauthn');

            // Prompt for biometric authentication
            const credential = await authenticateBiometric(options);

            // Verify authentication with server
            const verifyResponse = await fetch(`${APP_URL}/api/webauthn/auth/verify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    temp_token: tempToken,
                    credential
                })
            });

            if (!verifyResponse.ok) {
                const data = await verifyResponse.json();
                return { error: data.error || 'Authentication failed' };
            }

            const userData = await verifyResponse.json();
            setUser(userData.user || userData);

            // Set user in activity tracker
            const tracker = getGlobalTracker();
            const userObj = userData.user || userData;
            if (tracker && userObj.id) {
                tracker.setUser(userObj.id, userObj.organization_id);
                debugLog('✅ Activity tracker user context set (after passkey auth)');
            }

            return { success: true, user: userData.user || userData };

        } catch (err) {
            debugError('Passkey authentication error:', err);
            return { error: err.message || 'Passkey authentication failed' };
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Request email 2FA code during login flow (using temp_token)
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const requestEmailCodeWithToken = async (tempToken) => {
        if (!tempToken) {
            return { error: 'Session expired, please login again' };
        }

        setIsLoading(true);

        try {
            const response = await fetch(`${APP_URL}/api/webauthn/email/request`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ temp_token: tempToken })
            });

            if (!response.ok) {
                const data = await response.json();
                return { error: data.error || 'Failed to send email code' };
            }

            const data = await response.json();
            return { success: true, message: data.message || 'Email code sent' };

        } catch (err) {
            debugError('Request email code error:', err);
            return { error: 'Failed to send email code' };
        } finally {
            setIsLoading(false);
        }
    };

    // Expose function to manually trigger session expired redirect (for testing)
    const triggerSessionExpiredModal = useCallback(() => {
        debugLog('🧪 Manually triggering session expired redirect');
        setUser(null);
        clearAuthCookies();
        window.location.href = '/login?expired=true';
    }, []);

    /**
     * Gets the effective role for the current user, considering role emulation.
     * This is the single source of truth for determining which role to use.
     *
     * @returns {Promise<Object|null>} The effective role object (emulated or actual)
     */
    const getEffectiveRole = useCallback(async () => {
        // Get current user
        const currentUser = user || await getUser({ cache: true });

        if (!currentUser?.full_role) {
            return null;
        }

        // Check for emulated role
        const emulatedRoleId = localStorage.getItem('emulated_role_id');

        if (emulatedRoleId) {
            // Super Admin (role_id 6) - use hardcoded value since it may not exist in DB
            if (parseInt(emulatedRoleId) === 6) {
                return {
                    id: 6,
                    name: 'Super Admin',
                    all: true
                };
            }

            try {
                const response = await apiRequest(`/roles/${emulatedRoleId}`);

                if (response.ok) {
                    const emulatedRole = await response.json();
                    return emulatedRole;
                }
            } catch (err) {
                debugError('Failed to fetch emulated role:', err);
            }
        }

        // Return actual role
        return currentUser.full_role;
    }, [user, getUser, apiRequest]);

    /**
     * The context value object containing all authentication state and methods
     * @typedef {Object} AuthContextValue
     * @property {Object|null} user - Current cached user object (null if not authenticated)
     * @property {boolean} isLoading - Loading state for auth operations
     * @property {boolean} userLoading - Loading state specifically for user fetches
     * @property {boolean} isAuthenticated - Whether user is currently authenticated
     * @property {boolean} isLoggingOut - Whether logout is in progress
     * @property {Function} getUser - Async function to get user data with caching options
     * @property {Function} hasPermission - Check if user has specific permission
     * @property {Function} requirePermission - Throw error if permission not found
     * @property {Function} login - Authenticate user with username/password
     * @property {Function} logout - Log out current user
     * @property {Function} submit2FA - Submit 2FA verification code
     * @property {Function} refreshUser - Refresh user data from server
     * @property {Function} requestPasswordReset - Request password reset for email
     * @property {Function} authenticateWithBiometric - Authenticate with Touch ID/Face ID
     * @property {Function} registerBiometric - Register new biometric credential
     * @property {Function} getBiometricCredentials - Get list of user's biometric credentials
     * @property {Function} revokeBiometricCredential - Revoke a biometric credential
     * @property {Function} updateBiometricCredential - Update biometric credential name
     * @property {Function} apiRequest - Enhanced API request helper with auth
     * @property {Function} getCookie - Get cookie value by name
     * @property {Function} clearAuthCookies - Clear all authentication cookies
     * @property {string} APP_URL - Main API URL
     * @property {boolean} DEV - Whether in development mode
     * @property {string} FILE_APP_URL - File API URL
     */
    const value = useMemo(() => ({
        // State
        user,                    // Current cached user (can be null)
        isLoading,              // Loading state for auth operations
        userLoading,            // Loading state specifically for user fetches
        isAuthenticated: !!user,
        isLoggingOut,

        // Primary user function
        getUser,                // Async function to get user data

        // Permission functions
        hasPermission,          // Check if user has specific permission
        requirePermission,      // Throw error if permission not found
        getEffectiveRole,       // Get effective role (emulated or actual)

        // Actions
        login,
        logout,
        submit2FA,
        refreshUser,           // For backward compatibility
        requestPasswordReset,

        // WebAuthn / Biometric Authentication
        authenticateWithBiometric,
        registerBiometric,
        getBiometricCredentials,
        revokeBiometricCredential,
        updateBiometricCredential,

        // Passkey Setup During Login (uses temp_token)
        setupPasskeyDuringLogin,
        skipPasskeySetup,
        authenticatePasskeyWithToken,
        requestEmailCodeWithToken,

        // API utilities
        apiRequest,            // Enhanced API request helper

        // Utilities
        getCookie,
        clearAuthCookies,
        APP_URL,
        DEV,
        FILE_APP_URL,

        // App pause state (for background tab detection)
        isAppPaused,
        // setAppPaused, not setIsAppPaused: the ref must clear synchronously or
        // requests fired right after the resume click still see the stale value.
        resumeApp: () => setAppPaused(false),

        // Testing utilities (dev mode only)
        triggerSessionExpiredModal,

        // For backward compatibility (if needed)
        handle2FASubmit: submit2FA
    }), [
        user,
        isLoading,
        userLoading,
        isLoggingOut,
        getUser,
        hasPermission,
        requirePermission,
        getEffectiveRole,
        login,
        logout,
        submit2FA,
        refreshUser,
        requestPasswordReset,
        authenticateWithBiometric,
        registerBiometric,
        getBiometricCredentials,
        revokeBiometricCredential,
        updateBiometricCredential,
        setupPasskeyDuringLogin,
        skipPasskeySetup,
        authenticatePasskeyWithToken,
        requestEmailCodeWithToken,
        apiRequest,
        getCookie,
        clearAuthCookies,
        APP_URL,
        DEV,
        FILE_APP_URL,
        isAppPaused,
        setAppPaused,
        triggerSessionExpiredModal
    ]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};